import type { ReactNode } from 'react'

import { ApiError } from '@/lib/api'
import { cn } from '../lib/ui'

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn('size-4 animate-spin text-ink-400', className)}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  )
}

export function LoadingPane({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-10 text-sm text-ink-500">
      <Spinner />
      {label}…
    </div>
  )
}

/**
 * Turns any thrown value into one line a person can act on.
 *
 * A 402 is the free-tier gate and its message is user-facing copy the server
 * owns; a 419 is a stale CSRF token, which is a refresh rather than a logout.
 */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 419) return 'Your session moved on. Reload the page and try again.'
    if (error.status === 403) return error.message || 'You have read-only access to this workspace.'
    return error.message
  }
  if (error instanceof Error) return error.message
  return 'Something went wrong.'
}

export function ErrorBanner({
  error,
  onRetry,
  className,
}: {
  error: unknown
  onRetry?: () => void
  className?: string
}) {
  if (!error) return null
  const upgrade = error instanceof ApiError && error.isUpgradeRequired
  return (
    <div
      role="alert"
      className={cn(
        'flex items-start gap-3 rounded-card border px-3 py-2.5 text-sm',
        upgrade
          ? 'border-brand-200 bg-brand-50 text-brand-800 dark:border-brand-800 dark:bg-brand-950 dark:text-brand-200'
          : 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/50 dark:text-red-200',
        className,
      )}
    >
      <span className="min-w-0 flex-1">{errorMessage(error)}</span>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="shrink-0 rounded-md px-2 py-0.5 text-xs font-medium underline-offset-2 hover:underline"
        >
          Retry
        </button>
      )}
    </div>
  )
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center">
      <p className="text-sm font-medium text-ink-700 dark:text-ink-200">{title}</p>
      {description && <p className="max-w-sm text-sm text-ink-500">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  )
}
